// History: every broadcast with status, audience, timing and delivery stats.
// Actions: recipients list, edit (draft/scheduled), send now, retract/cancel, delete.
import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Eye, Send, Ban, Undo2, Trash2, Pencil, History, Filter, Inbox } from "lucide-react";
import Pagination from "../../../components/Pagination";
import { useBroadcastPage } from "../context";
import { broadcastApi } from "../api";
import { Modal, Btn, Select, Segmented, SectionCard, EmptyState, IconBtn } from "./ui";
import { STATUSES, STATUS_COLORS } from "../utils/labels";
import { rulesSummary } from "../utils/rules";
import { formatWhen } from "../utils/render";
import { severityColors } from "../../../components/Broadcasts/broadcastTheme";

const Bar = ({ value, total, color, label }) => {
  const { s } = useBroadcastPage();
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ minWidth: 130 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12, color: s.colors.muted, marginBottom: 4 }}>
        <span>{label}</span>
        <span style={{ direction: "ltr" }}>
          {value.toLocaleString()} · {pct}%
        </span>
      </div>
      <div style={s.bar}>
        <div style={s.barFill(pct, color)} />
      </div>
    </div>
  );
};

const RecipientsModal = ({ broadcast, onClose }) => {
  const { s, t, theme, isRTL, language } = useBroadcastPage();
  const H = t.history;
  const [filter, setFilter] = useState(broadcast.requireAck ? "unseen" : "all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    broadcastApi
      .recipients(broadcast.id, { filter, page, limit: 50 })
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [broadcast.id, filter, page]);

  const filters = ["all", "unseen", "seen", broadcast.requireAck ? "acknowledged" : "dismissed"];
  const title = language === "ar" && broadcast.titleAr ? broadcast.titleAr : broadcast.titleEn;

  return (
    <Modal title={H.recipientsTitle(title)} icon={Eye} onClose={onClose} maxWidth={900}>
      <div style={{ marginBottom: 16 }}>
        <Segmented
          value={filter}
          onChange={(k) => {
            setFilter(k);
            setPage(1);
          }}
          options={filters.map((k) => ({ value: k, label: H.filters[k] }))}
        />
      </div>
      {error && <div style={{ ...s.notice("warning"), marginBottom: 12 }}>{error}</div>}
      {!data && !error && <EmptyState>{H.loadingRecipients}</EmptyState>}
      {data && (
        <>
          <div style={{ ...s.tableWrap, maxHeight: "52vh", overflowY: "auto" }}>
            <table style={s.table(680)}>
              <thead>
                <tr>
                  <th style={s.th}>{H.colPerson}</th>
                  <th style={s.th}>{H.colRole}</th>
                  <th style={s.th}>{H.colClient}</th>
                  <th style={s.th}>{H.colSeen}</th>
                  <th style={s.th}>{broadcast.requireAck ? H.colAck : H.colDismissed}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ ...s.td, ...s.emptyState }}>
                      {H.nobody}
                    </td>
                  </tr>
                )}
                {data.items.map((r) => (
                  <tr key={r.userId} className="bc-row">
                    <td style={s.td}>
                      <div style={s.cellTitle}>{(language === "ar" && r.nameAr) || r.name || r.email || t.rules.user(r.userId)}</div>
                      {(r.name || r.nameAr) && r.email && <div style={s.cellMeta}>{r.email}</div>}
                    </td>
                    <td style={s.td}>{t.roles[String(r.role).toLowerCase()] || r.role}</td>
                    <td style={s.td}>{r.clientName || "—"}</td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>{formatWhen(r.readAt, language)}</td>
                    <td style={{ ...s.td, whiteSpace: "nowrap" }}>{formatWhen(broadcast.requireAck ? r.acknowledgedAt : r.dismissedAt, language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={data.pagination.page}
            totalPages={data.pagination.totalPages}
            onPageChange={setPage}
            theme={theme}
            isRTL={isRTL}
            style={{ marginTop: 14 }}
          />
        </>
      )}
    </Modal>
  );
};

const HistoryTab = ({ onEdit, refreshKey }) => {
  const { s, t, theme, isRTL, language, options, notify, refreshCounts } = useBroadcastPage();
  const H = t.history;
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [confirm, setConfirm] = useState(null); // { kind, b }
  const [busy, setBusy] = useState(false);
  const when = (d) => formatWhen(d, language);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await broadcastApi.list({ page, limit: 20, status: status || undefined }));
    } catch (e) {
      notify("error", e.message);
    } finally {
      setLoading(false);
    }
  }, [page, status, notify]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Keep stats moving while the tab is open.
  useEffect(() => {
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const act = async () => {
    const { kind, b } = confirm;
    setBusy(true);
    try {
      if (kind === "send") await broadcastApi.send(b.id);
      if (kind === "cancel" || kind === "retract") await broadcastApi.cancel(b.id);
      if (kind === "delete") await broadcastApi.remove(b.id);
      setConfirm(null);
      load();
      refreshCounts();
      notify("success", H.done[kind]);
    } catch (e) {
      notify("error", e.message);
    } finally {
      setBusy(false);
    }
  };

  const items = data?.items || [];
  const titleOf = (b) => (language === "ar" && b.titleAr ? b.titleAr : b.titleEn);

  return (
    <SectionCard
      icon={History}
      title={H.title}
      subtitle={H.subtitle}
      actions={
        <>
          <div style={{ minWidth: 190 }}>
            <Select
              ariaLabel={H.status}
              value={status}
              onChange={(v) => {
                setStatus(v ?? "");
                setPage(1);
              }}
              placeholder={H.allStatuses}
              options={[{ value: "", label: H.allStatuses }, ...STATUSES.map((k) => ({ value: k, label: t.statuses[k] }))]}
            />
          </div>
          <button type="button" className="bc-icon-btn" style={s.iconBtn()} onClick={load} aria-label={t.refresh} title={t.refresh}>
            <RefreshCw size={16} style={loading ? { animation: "bcSpin 1s linear infinite" } : null} />
          </button>
        </>
      }
    >
      {!data && loading && <EmptyState icon={Filter}>{t.loading}</EmptyState>}
      {data && items.length === 0 && <EmptyState icon={Inbox}>{H.empty}</EmptyState>}

      {items.length > 0 && (
        <div style={{ ...s.tableWrap, opacity: loading ? 0.7 : 1, transition: "opacity 0.2s ease" }}>
          <table style={s.table(980)}>
            <thead>
              <tr>
                <th style={s.th}>{H.colAnnouncement}</th>
                <th style={s.th}>{H.colStatus}</th>
                <th style={s.th}>{H.colAudience}</th>
                <th style={s.th}>{H.colWhen}</th>
                <th style={s.th}>{H.colDelivery}</th>
                <th style={s.th}>{H.colActions}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const expired = b.status === "SENT" && b.expiresAt && new Date(b.expiresAt) < new Date();
                const statusKey = expired ? "EXPIRED" : b.status;
                const accent = severityColors(b.severity, theme).accent;
                const meta = [
                  t.categories[b.category],
                  t.displayModes[b.displayMode]?.label,
                  b.requireAck ? t.templates.needsAck : null,
                  b.template ? H.fromTemplate(b.template.name) : null,
                ].filter(Boolean);
                return (
                  <tr key={b.id} className="bc-row">
                    <td style={{ ...s.td, maxWidth: 320 }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                        <span style={{ width: 8, height: 8, borderRadius: 99, background: accent, marginTop: 7, flexShrink: 0 }} aria-hidden="true" />
                        <div style={{ minWidth: 0 }}>
                          <div style={s.cellTitle}>{titleOf(b)}</div>
                          <div style={s.cellMeta}>{meta.join(" · ")}</div>
                        </div>
                      </div>
                    </td>
                    <td style={s.td}>
                      <span style={s.badge(STATUS_COLORS[statusKey] || STATUS_COLORS.DRAFT)}>{t.statuses[statusKey] || b.status}</span>
                      {b.status === "FAILED" && b.failureReason && (
                        <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6, maxWidth: 220, lineHeight: 1.5 }}>{b.failureReason}</div>
                      )}
                    </td>
                    <td style={{ ...s.td, fontSize: 13, color: s.colors.muted, maxWidth: 240 }}>
                      {b.audience ? b.audience.name : rulesSummary(b.rules, options, t)}
                    </td>
                    <td style={{ ...s.td, fontSize: 13, whiteSpace: "nowrap" }}>
                      <div style={{ color: s.colors.body }}>
                        {b.status === "SCHEDULED" ? H.scheduledAt(when(b.scheduledAt)) : b.sentAt ? H.sentAt(when(b.sentAt)) : H.createdAt(when(b.createdAt))}
                      </div>
                      {b.expiresAt && <div style={s.cellMeta}>{H.until(when(b.expiresAt))}</div>}
                      {b.createdBy?.email && <div style={s.cellMeta}>{H.by(b.createdBy.email)}</div>}
                    </td>
                    <td style={s.td}>
                      {b.sentAt ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: s.colors.text }}>{H.recipientsN(b.recipientCount)}</div>
                          <Bar label={H.seen} value={b.stats.seen} total={b.stats.total} color="#2e7cba" />
                          {b.requireAck && <Bar label={H.acknowledged} value={b.stats.acknowledged} total={b.stats.total} color="#10b981" />}
                        </div>
                      ) : (
                        <span style={{ color: s.colors.muted }}>—</span>
                      )}
                    </td>
                    <td style={{ ...s.td, width: 1 }}>
                      <div style={{ ...s.actionsCell, flexWrap: "nowrap" }}>
                        {b.sentAt && (
                          <button type="button" className="bc-action-btn" style={s.actionBtn("primary")} onClick={() => setViewing(b)}>
                            <Eye size={14} /> {H.recipients}
                          </button>
                        )}
                        {["DRAFT", "SCHEDULED"].includes(b.status) && (
                          <>
                            <IconBtn icon={Pencil} label={t.edit} onClick={() => onEdit(b)} />
                            <IconBtn icon={Send} label={H.sendNow} onClick={() => setConfirm({ kind: "send", b })} />
                          </>
                        )}
                        {b.status === "SCHEDULED" && <IconBtn icon={Ban} label={H.cancel} tone="danger" onClick={() => setConfirm({ kind: "cancel", b })} />}
                        {b.status === "SENT" && !expired && (
                          <button type="button" className="bc-action-btn" style={s.actionBtn("danger")} onClick={() => setConfirm({ kind: "retract", b })}>
                            <Undo2 size={14} /> {H.retract}
                          </button>
                        )}
                        {["DRAFT", "CANCELLED", "FAILED"].includes(b.status) && (
                          <IconBtn icon={Trash2} label={t.delete} tone="danger" onClick={() => setConfirm({ kind: "delete", b })} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data?.pagination && (
        <Pagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
          onPageChange={setPage}
          theme={theme}
          isRTL={isRTL}
          disabled={loading}
          style={{ marginTop: 16 }}
        />
      )}

      {viewing && <RecipientsModal broadcast={viewing} onClose={() => setViewing(null)} />}
      {confirm && (
        <Modal
          title={H.confirm[confirm.kind].title}
          icon={{ send: Send, cancel: Ban, retract: Undo2, delete: Trash2 }[confirm.kind]}
          onClose={() => setConfirm(null)}
          busy={busy}
          footer={
            <>
              <Btn kind="secondary" disabled={busy} onClick={() => setConfirm(null)}>
                {t.back}
              </Btn>
              <Btn kind={confirm.kind === "send" ? "success" : "danger"} disabled={busy} onClick={act}>
                {busy ? t.working : H.confirm[confirm.kind].btn}
              </Btn>
            </>
          }
        >
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7 }}>
            <strong style={{ color: s.colors.text }}>{titleOf(confirm.b)}</strong>
            <br />
            {H.confirm[confirm.kind].body}
          </p>
        </Modal>
      )}
    </SectionCard>
  );
};

export default HistoryTab;
